/**
 * Parse a string describing measure ranges and signatures
 * e.g. "1-12 => 4/4" or "21 => 5/16"
 */
export function parseMeasureSignatureLine(line) {
  // 1) Extract measure range using a quick regex:
  const rangeRegex = /(\d+)(?:\s*\-\s*(\d+))?/;
  const matchRange = rangeRegex.exec(line);
  if (!matchRange) return null;

  const startStr = matchRange[1];
  const endStr = matchRange[2];

  const startMeasure = parseInt(startStr, 10);
  const endMeasure = endStr ? parseInt(endStr, 10) : startMeasure;

  // 2) Extract time sig (numerator/denominator) e.g. "4/4"
  const timeSigRegex = /(\d+)\/(\d+)/;
  const matchTS = timeSigRegex.exec(line);
  if (!matchTS) return null;

  const numerator = parseInt(matchTS[1], 10);
  const denominator = parseInt(matchTS[2], 10);

  // 3) Optional trailing options after a pipe:
  // Examples:
  //   "1-4 => 5/8 | group=2+3; removeNonAccents"
  //   "9 => 7/8 | group=3+2+2"
  //   "3-6 => 12/8 | removeNonAccents=false"
  let grouping = null;
  let removeNonAccents; // undefined if not specified
  const optMatch = /\|\s*(.*)$/.exec(line);
  if (optMatch) {
    const opts = optMatch[1]
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    for (const opt of opts) {
      if (opt.toLowerCase().startsWith("group=")) {
        grouping = opt.split("=")[1]?.trim() || null;
      } else if (opt.toLowerCase().startsWith("removenonaccents")) {
        // support "removeNonAccents" and "removeNonAccents=true/false"
        const parts = opt.split("=");
        if (parts.length === 1) {
          removeNonAccents = true;
        } else {
          const v = parts[1].trim().toLowerCase();
          removeNonAccents = !(v === "false" || v === "0" || v === "no");
        }
      }
    }
  }

  return {
    startMeasure,
    endMeasure,
    numerator,
    denominator,
    grouping,
    removeNonAccents,
  };
}

/**
 * Parse a string describing measure ranges
 * e.g. "1,3,5-7" into array [1,3,5,6,7]
 */
export function parseMeasureRanges(inputStr) {
  const bars = [];
  if (!inputStr || !inputStr.trim()) return bars;

  const segments = inputStr.split(",");
  segments.forEach((seg) => {
    seg = seg.trim();
    if (!seg) return;

    if (seg.includes("-")) {
      const [startRaw, endRaw] = seg.split("-").map((x) => x.trim());
      const start = parseInt(startRaw, 10); // convert to zero-based
      const end = parseInt(endRaw, 10);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        for (let i = start; i <= end; i++) {
          bars.push(i);
        }
      }
    } else {
      const val = parseInt(seg, 10);
      if (!isNaN(val)) bars.push(val);
    }
  });
  return Array.from(new Set(bars)).sort((a, b) => a - b);
}

/**
 * Parse a string containing accent patterns
 */
export function parseAccentString(str) {
  // Parse comma separated values, allowing ranges
  const results = [];
  if (!str.trim()) return results;

  const segments = str.split(",");
  segments.forEach((seg) => {
    seg = seg.trim();
    if (!seg) return;

    if (seg.includes("-")) {
      // handle range e.g. "2-5"
      const [startRaw, endRaw] = seg.split("-").map((x) => x.trim());
      const start = parseInt(startRaw, 10);
      const end = parseInt(endRaw, 10);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        for (let i = start; i <= end; i++) {
          results.push(i);
        }
      }
    } else {
      // single number
      const val = parseInt(seg, 10);
      if (!isNaN(val)) {
        results.push(val);
      }
    }
  });

  // Return only unique, valid measure numbers (filter out zeros or negatives)
  return [...new Set(results)].filter((m) => m > 0);
}

/**
 * Parse a beat input string into an array of beat numbers
 */
export function parseBeatsInput(str) {
  const beats = [];
  if (!str.trim()) return beats;
  const parts = str.split(",");
  for (let item of parts) {
    item = item.trim();
    if (!item) continue;

    // handle ranges like "2-4"
    if (item.includes("-")) {
      const [startRaw, endRaw] = item.split("-").map((s) => s.trim());
      const start = parseInt(startRaw, 10);
      const end = parseInt(endRaw, 10);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        for (let j = start; j <= end; j++) {
          beats.push(j);
        }
      }
    } else {
      // single number
      const n = parseInt(item, 10);
      if (!isNaN(n) && n > 0) {
        beats.push(n);
      }
    }
  }
  return Array.from(new Set(beats)).sort((a, b) => a - b);
}

/**
 * Parse a string into a bar representation
 */
export function parseBarString(str) {
  let resultString = "";
  const trimmed = str.trim();
  let i = 0;
  while (i < trimmed.length) {
    if (i + 1 < trimmed.length) {
      const twoDigit = trimmed.substring(i, i + 2);
      const val = parseInt(twoDigit, 10);
      if (!isNaN(val) && val >= 10 && val <= 99) {
        if (resultString) resultString += ",";
        resultString += val;
        i += 2;
        continue;
      }
    }
    if (resultString) resultString += ",";
    resultString += trimmed[i];
    i += 1;
  }
  return parseBeatsInput(resultString);
}
